//
// Copyright (c) 2011-2024 by Curalate, Inc.
//
import { useState } from 'react';

import { DEFAULT_PAGE_SIZE } from '@apis/campaign-requests/shared/Pagination';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { getFuzzySearch } from '@utils/getFuzzySearch';

const FUSE_THRESHOLD = 0.3;
const EXACT_MATCH_FUSE_THRESHOLD = 0;

interface UseSortAndPaginateOptions<T, TSortKey extends keyof T> {
  items?: T[];
  initialSortKey?: TSortKey;
  initialSortDir?: SortDir;
  initialItemsPerPage?: number;
  searchKeys?: (keyof T)[];
  exactMatchSearchKeys?: (keyof T)[];
  isSortingEnabled?: boolean;
  isPaginationEnabled?: boolean;
}

/**
 * Custom hook to handle frontend sorting, pagination, and fuzzy search
 *
 * @template T - The type of the items being processed.
 * @template TSortKey - The keys of T that can be used for sorting.
 *
 * @param options - Configuration options for the hook.
 * @param options.items - The items to be processed.
 * @param options.initialSortKey - The initial key to sort the items by.
 * @param options.initialSortDir - The initial direction to sort the items.
 * @param options.initialItemsPerPage - The initial number of items per page.
 * @param options.searchKeys - The keys of T that can be used for fuzzy search.
 * @param options.exactMatchSearchKeys - The keys of T that can be used for exact match search.
 * @param options.isSortingEnabled - Enable or disable sorting.
 * @param options.isPaginationEnabled - Enable or disable pagination.
 *
 * @returns {object} An object containing:
 *                   - `processedItems`: The processed items after sorting, searching, and pagination.
 *                   - `totalItems`: The total number of items after filtering.
 *                   - `searchTerm`: The current search term.
 *                   - `sortKey`: The current sort key.
 *                   - `sortDir`: The current sort direction.
 *                   - `currentPage`: The current page (if pagination is enabled).
 *                   - `itemsPerPage`: The number of items per page (if pagination is enabled).
 *                   - Setter functions to update state: `setSearchTerm`, `setSort`, `setCurrentPage`, `setItemsPerPage`.
 */
export const useSortAndPaginate = <T, TSortKey extends keyof T>(
  options: UseSortAndPaginateOptions<T, TSortKey>,
) => {
  const {
    exactMatchSearchKeys = [],
    initialItemsPerPage = DEFAULT_PAGE_SIZE,
    initialSortDir,
    initialSortKey,
    isPaginationEnabled = true,
    isSortingEnabled = true,
    items = [],
    searchKeys = [],
  } = options;

  const [sortKey, setSortKey] = useState<TSortKey | undefined>(initialSortKey);
  const [sortDir, setSortDir] = useState<SortDir | undefined>(initialSortDir);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  const [searchTerm, setSearchTerm] = useState('');

  const itemsToSearch = items;

  const getExactMatchItems = (items: T[], searchTerm: string) => {
    return getFuzzySearch({
      itemsToSearch: items,
      searchKeys: exactMatchSearchKeys as string[],
      searchTerm,
      threshold: EXACT_MATCH_FUSE_THRESHOLD,
    });
  };

  const getFuzzyMatchItems = (
    items: T[],
    searchTerm: string,
    exactMatchItems: T[],
  ) => {
    return getFuzzySearch({
      itemsToSearch: items.filter((item) => !exactMatchItems.includes(item)),
      searchKeys: searchKeys as string[],
      searchTerm,
      threshold: FUSE_THRESHOLD,
    });
  };

  const getItemToSearch = () => {
    if (searchTerm) {
      const exactMatchItems = getExactMatchItems(itemsToSearch, searchTerm);
      const fuzzySearchItems = getFuzzyMatchItems(
        itemsToSearch,
        searchTerm,
        exactMatchItems,
      );
      return [...exactMatchItems, ...fuzzySearchItems];
    } else {
      return [...itemsToSearch];
    }
  };

  // Perform fuzzy search if a search term is provided; otherwise, use all items
  const fuzzySearchItems = getItemToSearch();

  const processedItems = (() => {
    if (isSortingEnabled && sortKey) {
      const sortedItems = [...fuzzySearchItems].sort((a, b) => {
        const compareKeyA = a[sortKey] ?? '';
        const compareKeyB = b[sortKey] ?? '';

        if (compareKeyA < compareKeyB) {
          return -1;
        }
        if (compareKeyA > compareKeyB) {
          return 1;
        }
        return 0;
      });

      if (sortDir === SortDir.DESC) {
        sortedItems.reverse();
      }

      return sortedItems;
    } else {
      return fuzzySearchItems;
    }
  })();

  const paginatedItems = (() => {
    if (isPaginationEnabled) {
      const startIndex = currentPage * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return processedItems.slice(startIndex, endIndex);
    } else {
      return processedItems;
    }
  })();

  /**
   * Updates the number of items per page and resets the current page to 0.
   * This ensures that the pagination stays consistent when the number of items per page changes.
   * Without this reset, the current page might point to a page number that does not exist in the new pagination.
   */
  const updateItemsPerPage = (newItemsPerPage: number) => {
    if (itemsPerPage !== newItemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(0);
    }
  };

  /**
   * Updates the search term and resets the current page to 0.
   * This ensures that the pagination stays consistent when the search term changes.
   * Without this reset, the current page might point to a page number that does not exist in the new pagination.
   */
  const updateSearchTerm = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(0);
  };

  const updateSort = (newSortKey: TSortKey, newSortDir: SortDir) => {
    setSortKey(newSortKey);
    setSortDir(newSortDir);
  };

  return {
    currentPage,
    itemsPerPage,
    processedItems: paginatedItems,
    searchTerm,
    setCurrentPage,
    setItemsPerPage: updateItemsPerPage,
    setSearchTerm: updateSearchTerm,
    setSort: updateSort,
    sortDir,
    sortKey,
    totalItems: processedItems.length,
  };
};
